import { inject, Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  limit,
} from '@angular/fire/firestore';
import { Observable, from, map } from 'rxjs';
import { Review } from '../models/review';

@Injectable({ providedIn: 'root' })
export class ReviewService {
  private firestore = inject(Firestore);

  private reviewsCollection = collection(this.firestore, 'reviews');

  /**
   * Get only 6 reviews
   */
  getLimitedReviews(): Observable<Review[]> {
    const q = query(this.reviewsCollection, limit(6));
    return collectionData(q, { idField: 'id' }) as Observable<Review[]>;
  }

  /**
   * Get all reviews
   */
  getReviews(): Observable<Review[]> {
    return collectionData(this.reviewsCollection, { idField: 'id' }) as Observable<Review[]>;
  }

  /**
   * Get a review by ID
   */
  getReview(id: string): Observable<Review | undefined> {
    const ref = doc(this.firestore, `reviews/${id}`);
    return from(getDoc(ref)).pipe(
      map(snapshot =>
        snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as Review) : undefined
      )
    );
  }

  /**
   * Add a new review
   */
  addReview(review: Review) {
    return from(addDoc(this.reviewsCollection, review));
  }

  /**
   * Update an existing review
   */
  updateReview(id: string, review: Partial<Review>) {
    const ref = doc(this.firestore, `reviews/${id}`);
    return from(updateDoc(ref, review as any));
  }

  /**
   * Delete a review
   */
  deleteReview(id: string) {
    const ref = doc(this.firestore, `reviews/${id}`);
    return from(deleteDoc(ref));
  }
}
